import React, { useEffect, useMemo, useState } from "react";
import s from "../styles.module.css";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ShareIcon from "@mui/icons-material/Share";
import { ImageCarousel } from "../../../shared/components/ImageCarousel/ui/ImageCarousel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import DeletePropertyModal from "../../../shared/components/Modal/DeletePropertyModal/DeletePropertyModal";
import ShowModal from "../../../shared/components/Modal/ShowModal/ShowModal";
import TransferModal from "../../../shared/components/Modal/TransferModal/TransferModal";
import { toast } from "react-toastify";
import { detailsFields } from "../../../data/Fields";
import { formatDate } from "../../../shared/components/FormatDate";
import MapDetail from "../mapContent/MapDetail";
import useAuth from "../../../hooks/useAuth";

const ObjectDetails = () => {
  const { property_type, id } = useParams();
  const { auth } = useAuth();
  const token = auth?.access_token;
  const userRole = auth?.role;
  const navigate = useNavigate();
  const currentUserId = auth?.agent_id;
  const [property, setProperty] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isShowModalOpen, setIsShowModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const endpoint = useMemo(
    () =>
      `${process.env.REACT_APP_API_URL}/api/v1/property/get_property/${property_type}/${id}`,
    [property_type, id],
  );

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) throw new Error("Объект удален");
        const data = await response.json();
        setProperty(data?.property);
      } catch (error) {
        console.error("Ошибка:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPropertyData();
  }, [token, endpoint]);

  const copyLink = () => {
    const link = window.location.href;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.success("Ссылка скопирована в буфер обмена!");
      })
      .catch((err) => {
        console.error("Ошибка при копировании ссылки: ", err);
      });
  };

  const filteredDetailsFields = useMemo(() => {
    if (!property) return [];

    return detailsFields.filter((field) => {
      // Исключаем ненужные поля для участков
      if (
        property.property_type === "Дома и участки" &&
        property.land_type === "Участок"
      ) {
        const excludedFields = ["rooms", "area", "furniture"];
        if (excludedFields.includes(field.name)) {
          return false;
        }
      }

      // Показываем только если есть значение
      return (
        property[field.name] !== null &&
        property[field.name] !== undefined &&
        property[field.name] !== ""
      );
    });
  }, [property]);

  if (loading)
    return (
      <div className="loadingContainer">
        <div className="loader"></div>
      </div>
    );

  if (error) {
    return (
      <div
        className="errorContainer"
        style={{ textAlign: "center", marginTop: 50 }}
      >
        <p style={{ color: "red", fontSize: 18 }}>
          {error === "property not found" ? "Объект не найден" : error}
        </p>
        <button onClick={() => navigate(-1)} className={s.backButton}>
          <ArrowBackIcon sx={{ fontSize: 20 }} />
          <span>Назад</span>
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      <header>
        <button className={s.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon sx={{ fontSize: 20 }} />
          <span>Назад</span>
        </button>
        <div className={s.titleContainer}>
          <h1 className="title">
            {property.property_type === "Дома и участки"
              ? property.land_type
              : property.property_type}

            {property.rooms && `, ${property.rooms} комн`}
            {property.area > 0 && `, ${property.area} м²`}
            {property.property_type === "Дома и участки" &&
              property.land_type === "Участок" &&
              property.hundredth &&
              `, ${property.hundredth} сот.`}
          </h1>

          <div className={s.metaInfo}>
            <span className={s.id}>ID: {property.id}</span>
            <div className={s.date}>
              <CalendarMonthIcon
                sx={{ color: "rgba(32, 32, 31, 1)", fontSize: 22 }}
              />
              <span>{formatDate(property.created_at)}</span>
            </div>
            <ShareIcon
              onClick={copyLink}
              sx={{
                color: "rgba(32, 32, 31, 1)",
                fontSize: 38,
                backgroundColor: "rgba(236, 236, 236, 1)",
                borderRadius: "8px",
                padding: "6px",
                cursor: "pointer",
              }}
            />
          </div>
        </div>
      </header>

      <main className={s.main}>
        <div>
          <section className={s.details}>
            <div className={s.detailsGrid}>
              {filteredDetailsFields.map((field) => {
                if (field.name === "description") {
                  const copyDescriptionToClipboard = () => {
                    const textToCopy = `${property[field.name]}\nID: ${property.id}`;
                    navigator.clipboard
                      .writeText(textToCopy)
                      .then(() => {
                        toast.success("Описание скопировано!");
                      })
                      .catch((err) => {
                        console.error("Ошибка копирования: ", err);
                        toast.error("Не удалось скопировать");
                      });
                  };

                  return (
                    <div key={field.name} className={s.descriptionBlock}>
                      <div className={s.descriptionHeader}>
                        <label>{field.label}:</label>
                        <button
                          onClick={copyDescriptionToClipboard}
                          style={{
                            fontSize: 18,
                            cursor: "pointer",
                            color: "#555",
                            marginLeft: "8px",
                          }}
                        >
                          📋
                        </button>
                      </div>
                      <p className={s.descriptionText}>
                        {property[field.name]}
                        <br />
                        <br />
                        <span>ID: {property.id}</span>
                      </p>
                    </div>
                  );
                }
                return (
                  <div key={field.name} className={s.detailItem}>
                    <label>{field.label}:</label>
                    <strong>
                      {field.name === "price" || field.name === "on_hand"
                        ? `${property[field.name]} $`
                        : property[field.name]}
                    </strong>
                  </div>
                );
              })}
            </div>
            <MapDetail
              latitude={property?.latitude}
              longitude={property?.longitude}
            />
            <div className={s.buttons}>
              {(userRole === "admin" ||
                (property.agent &&
                  Number(currentUserId) === property.agent.id)) && (
                <button
                  onClick={() =>
                    navigate(`/edit/${property_type}/${id}`, {
                      state: { initialData: property },
                    })
                  }
                >
                  Редактировать
                </button>
              )}
              <button onClick={() => setIsShowModalOpen(true)}>
                Назначить показ
              </button>
              {userRole === "super_admin" && (
                <button onClick={() => setIsTransferModalOpen(true)}>
                  Перенос объекта
                </button>
              )}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 20,
              }}
            >
              <button
                style={{
                  background: "rgba(218, 86, 119, 1)",
                  borderRadius: 5,
                  padding: 15,
                  fontSize: 18,
                  fontWeight: 500,
                  color: "#fff",
                  cursor: "pointer",
                }}
                onClick={() => setOpenModal(true)}
              >
                Удалить объявление
              </button>
            </div>
          </section>
        </div>

        <div className={s.imgBlock}>
          {Array.isArray(property.photos) && property.photos.length > 0 ? (
            <ImageCarousel
              photo={property.photos[0]}
              photos={property.photos}
            />
          ) : (
            <p>Нет фотографий</p>
          )}
        </div>
      </main>

      <ShowModal
        open={isShowModalOpen}
        onClose={() => setIsShowModalOpen(false)}
        id={property?.id}
      />
      <TransferModal
        open={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        propertyId={property?.property_id}
        endpointType={property_type}
      />
      <DeletePropertyModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        id={property?.id}
      />
    </div>
  );
};

export default ObjectDetails;
